/**
 * Print-on-demand services the export can be shaped for (docs/00-decisions.md — "Print services").
 *
 * freeAlbum is not a print shop; the PDF is the product and it goes wherever the person likes. A
 * service profile is what one shop's upload form expects — bleed, marks, page mode, how the cover
 * is delivered, the spine width for a page count, the board a hardcover wraps around — written down
 * once so the wizard, the editor's guides, the export dialog, the worker and the render page all
 * agree, and so the next shop is one more entry here.
 *
 * Every figure below comes from the shop's own published specifications (URLs on each profile),
 * measured on 2026-09-13. Sizes are in millimetres like everything else; the inch figures are what
 * the shop calls them.
 */
import { innerPageCount } from './helpers';
import { applyCommand } from './ops';
import type { AlbumDocument, ExportOptions, PrintTarget } from './schema';

export const MM_PER_INCH = 25.4;

export type PrintServiceId = 'prestophoto' | 'blurb' | 'lulu';
export type Binding = PrintTarget['binding'];
/** how the cover reaches the shop: first/last PDF page (their form adds wrap + spine), a wrap file, or not at all */
export type CoverMode = 'bound' | 'separate' | 'none';

/** Extra sheet beyond the trim, per physical edge of the sheet, in mm. */
export interface Bleed {
  topMm: number;
  rightMm: number;
  bottomMm: number;
  leftMm: number;
}

export const uniformBleed = (mm: number): Bleed => ({ topMm: mm, rightMm: mm, bottomMm: mm, leftMm: mm });
export const NO_BLEED: Bleed = uniformBleed(0);
export const isUniformBleed = (b: Bleed): boolean => b.topMm === b.rightMm && b.topMm === b.bottomMm && b.topMm === b.leftMm;

export interface PrintServiceSize {
  widthIn: number;
  heightIn: number;
  widthMm: number;
  heightMm: number;
  /**
   * Hardcover boards that are bigger than the pages (Blurb's ImageWrap): extra board width per
   * outer side and extra height in total, so the wrap file grows by that before its own bleed.
   */
  boardExtraMm?: { perSideW: number; totalH: number };
}

export interface CoverSpec {
  /** how this shop can take the cover for this binding; the first entry is the default */
  modes: readonly CoverMode[];
  /** sheet beyond our [panel][spine][panel] strip, all round: bleed for a softcover, the turn-in for a hardcover */
  wrapMm: number;
  /** spine width in mm for an inner page count; `estimated` when the shop publishes no figure */
  spineMm: (pages: number) => number;
  spineEstimated?: boolean;
  /** pages the shop binds this way */
  pages: { min: number; max: number };
  /** spine text is typed into the shop's form (they cannot promise where a printed one lands) */
  spineTextOnForm?: boolean;
  /** the shop's name for this binding, for the note */
  label: string;
}

export interface PrintServiceProfile {
  id: PrintServiceId;
  url: string;
  /** what every page sheet grows by; `outside` = top, bottom and fore-edge only, nothing at the gutter */
  page: { bleedMm: number; edges: 'all' | 'outside' };
  /** keep faces and words this far inside the trim; the binding edge may want more */
  safeMarginMm: number;
  gutterSafeMarginMm?: number;
  pageMode: ExportOptions['pageMode'];
  cropMarks: false;
  /** the shop binds on the left; a right-to-left book is emitted back to front (see resolveExport) */
  leftBound: boolean;
  covers: Partial<Record<Binding, CoverSpec>>;
  sizes: PrintServiceSize[];
}

const inches = (widthIn: number, heightIn: number, boardExtraMm?: PrintServiceSize['boardExtraMm']): PrintServiceSize => ({
  widthIn,
  heightIn,
  widthMm: Math.round(widthIn * MM_PER_INCH * 10) / 10,
  heightMm: Math.round(heightIn * MM_PER_INCH * 10) / 10,
  ...(boardExtraMm ? { boardExtraMm } : {}),
});
const mmSize = (widthMm: number, heightMm: number, boardExtraMm?: PrintServiceSize['boardExtraMm']): PrintServiceSize => ({
  widthIn: Math.round((widthMm / MM_PER_INCH) * 100) / 100,
  heightIn: Math.round((heightMm / MM_PER_INCH) * 100) / 100,
  widthMm,
  heightMm,
  ...(boardExtraMm ? { boardExtraMm } : {}),
});

/** `[fromPages, toPages, spineMm]` brackets → a lookup that clamps outside the table. */
function spineTable(rows: readonly (readonly [number, number, number])[]): (pages: number) => number {
  return (pages) => {
    for (const [from, to, mm] of rows) if (pages >= from && pages <= to) return mm;
    if (pages < rows[0]![0]) return rows[0]![2];
    return rows[rows.length - 1]![2];
  };
}

/** Straight lines between measured `[pages, mm]` points, extended at both ends. */
function spineCurve(points: readonly (readonly [number, number])[]): (pages: number) => number {
  return (pages) => {
    const round = (v: number) => Math.round(v * 100) / 100;
    if (pages <= points[0]![0]) {
      const [[p0, m0], [p1, m1]] = [points[0]!, points[1]!];
      return round(Math.max(1, m0 - ((p0 - pages) * (m1 - m0)) / (p1 - p0)));
    }
    for (let i = 1; i < points.length; i++) {
      const [p0, m0] = points[i - 1]!;
      const [p1, m1] = points[i]!;
      if (pages <= p1) return round(m0 + ((pages - p0) * (m1 - m0)) / (p1 - p0));
    }
    const [[p0, m0], [p1, m1]] = [points[points.length - 2]!, points[points.length - 1]!];
    return round(m1 + ((pages - p1) * (m1 - m0)) / (p1 - p0));
  };
}

/** Lulu's paperback formula: pages / 17.48 + 1.524 mm. A fair estimate for any 120 gsm perfect-bound softcover. */
const perfectBoundSpine = (pages: number): number => Math.round((pages / 17.48 + 1.524) * 100) / 100;

export const PRINT_SERVICES: Readonly<Record<PrintServiceId, PrintServiceProfile>> = {
  prestophoto: {
    id: 'prestophoto',
    url: 'https://www.prestophoto.com/products/pdf/',
    // help/PDF+Specs: "Set bleed @ 1/8" symmetric all around"; 1/4" caution area
    page: { bleedMm: MM_PER_INCH / 8, edges: 'all' },
    safeMarginMm: MM_PER_INCH / 4,
    pageMode: 'single',
    cropMarks: false,
    leftBound: true,
    covers: {
      softcover: {
        modes: ['bound', 'separate'],
        wrapMm: MM_PER_INCH / 8,
        spineMm: perfectBoundSpine,
        spineEstimated: true,
        pages: { min: 13, max: 500 },
        spineTextOnForm: true,
        label: 'Imagewrap softcover',
      },
      hardcover: {
        modes: ['bound', 'separate'],
        // help/Book+Binding+Specifications: "safety margin for turn-in (Bleed) for all book sizes of 1.0""
        wrapMm: MM_PER_INCH,
        // hardcover spine table, Silk 120 paper
        spineMm: spineTable([
          [26, 78, 9], [80, 114, 11], [116, 150, 13], [152, 188, 15], [190, 224, 17], [226, 260, 19],
          [262, 298, 21], [300, 334, 23], [336, 370, 25], [372, 408, 27], [410, 440, 29],
        ]),
        pages: { min: 13, max: 500 },
        spineTextOnForm: true,
        label: 'Imagewrap hardcover',
      },
    },
    sizes: [
      inches(8.5, 8.5), inches(10, 10), inches(12, 12),
      inches(7, 9), inches(8, 10), inches(8.5, 11), inches(9, 12),
      inches(5, 7), inches(5.5, 8.5), inches(6, 9), inches(8, 12), inches(8.3, 11.7),
      inches(8, 6), inches(9, 7), inches(10, 8), inches(11, 8.5), inches(12, 9), inches(13, 10), inches(14, 11),
      inches(9.5, 8), inches(11, 9.25), inches(13, 11),
      inches(7, 5), inches(9, 6), inches(11.7, 8.3), inches(12, 8), inches(14, 10),
      inches(10.5, 6), inches(12, 6.75), inches(13, 7.25), inches(14, 8),
    ],
  },
  blurb: {
    id: 'blurb',
    url: 'https://www.blurb.com/pdf-to-book',
    // make/pdf_to_book/booksize_calculator: page PDF = trim + 0.317 cm on top, bottom and outside edge; safe 0.635, binding edge 1.27
    page: { bleedMm: 3.17, edges: 'outside' },
    safeMarginMm: 6.35,
    gutterSafeMarginMm: 12.7,
    pageMode: 'single',
    cropMarks: false,
    leftBound: true,
    covers: {
      softcover: {
        modes: ['separate'],
        wrapMm: 3.17,
        spineMm: spineCurve([[40, 3.17], [100, 6.35]]),
        pages: { min: 20, max: 440 },
        label: 'Softcover',
      },
      hardcover: {
        modes: ['separate'],
        // ImageWrap: 0.776 cm beyond the board on every edge; the board itself is bigger than the page (per size below)
        wrapMm: 7.76,
        spineMm: spineCurve([[40, 11.64], [100, 14.46], [200, 19.05], [440, 31.4]]),
        pages: { min: 20, max: 440 },
        label: 'ImageWrap hardcover',
      },
    },
    // trims as the calculator states them; the names are the marketing sizes
    sizes: [
      mmSize(127, 127),
      mmSize(171.45, 168.28, { perSideW: 6.0, totalH: 9.87 }),
      mmSize(203.2, 254, { perSideW: 6.35, totalH: 9.51 }),
      mmSize(241.3, 203.2, { perSideW: 6.7, totalH: 9.87 }),
      mmSize(298.45, 298.45, { perSideW: 4.23, totalH: 9.87 }),
      mmSize(317.5, 269.88, { perSideW: 6.35, totalH: 9.87 }),
    ],
  },
  lulu: {
    id: 'lulu',
    url: 'https://www.lulu.com/create/photo-books',
    // Book Creation Guide: interior = trim + 0.125 in each side; safety margin 0.5 in
    page: { bleedMm: 3.175, edges: 'all' },
    safeMarginMm: 12.7,
    pageMode: 'single',
    cropMarks: false,
    leftBound: true,
    covers: {
      softcover: {
        modes: ['separate', 'none'],
        wrapMm: 3.175,
        spineMm: perfectBoundSpine,
        pages: { min: 32, max: 800 },
        label: 'Paperback',
      },
      hardcover: {
        modes: ['separate', 'none'],
        // help: "A hardcover is printed 0.75" larger than your front cover trim size"
        wrapMm: MM_PER_INCH * 0.75,
        spineMm: spineTable([
          [24, 84, 6], [85, 140, 13], [141, 168, 16], [169, 194, 17], [195, 222, 19], [223, 250, 21], [251, 278, 22],
          [279, 306, 24], [307, 334, 25], [335, 360, 27], [361, 388, 29], [389, 416, 30], [417, 444, 32], [445, 472, 33], [473, 500, 35],
        ]),
        pages: { min: 24, max: 800 },
        label: 'Hardcover casewrap',
      },
    },
    sizes: [
      mmSize(190.5, 190.5), mmSize(216, 216), mmSize(216, 279), mmSize(279, 216), mmSize(229, 178),
      mmSize(210, 297), mmSize(297, 210), mmSize(178, 254), mmSize(189, 246), mmSize(156, 234),
      mmSize(168, 260), mmSize(152, 229), mmSize(140, 216), mmSize(127, 203), mmSize(108, 175),
    ],
  },
};

export const PRINT_SERVICE_IDS = Object.keys(PRINT_SERVICES) as PrintServiceId[];

export function printService(id: ExportOptions['printService'] | PrintTarget['service'] | null | undefined): PrintServiceProfile | null {
  return id && id !== 'none' ? (PRINT_SERVICES[id] ?? null) : null;
}

export interface ServiceSizeMatch {
  size: PrintServiceSize;
  /** both sides within a millimetre */
  exact: boolean;
  /** how far the aspect ratios are apart, as a fraction (0.05 = 5 %) */
  ratioGap: number;
}

/** The shop's size closest in shape to the album's page, then closest in area. */
export function nearestServiceSize(service: PrintServiceProfile, page: { widthMm: number; heightMm: number }): ServiceSizeMatch {
  const ratio = page.widthMm / page.heightMm;
  const area = page.widthMm * page.heightMm;
  const score = (s: PrintServiceSize) => Math.abs(Math.log(s.widthMm / s.heightMm / ratio)) * 10 + Math.abs(Math.log((s.widthMm * s.heightMm) / area));
  let best = service.sizes[0]!;
  for (const size of service.sizes) if (score(size) < score(best)) best = size;
  return {
    size: best,
    exact: Math.abs(best.widthMm - page.widthMm) <= 1 && Math.abs(best.heightMm - page.heightMm) <= 1,
    ratioGap: Math.abs(best.widthMm / best.heightMm / ratio - 1),
  };
}

/** The export as it will actually run: the person's options with the shop's rules laid over them. */
export interface ResolvedExport {
  service: PrintServiceProfile | null;
  binding: Binding;
  /** the shop's cover rules for this binding, or null without a service */
  coverSpec: CoverSpec | null;
  dpi: number;
  jpegQuality: number;
  pageMode: ExportOptions['pageMode'];
  cover: CoverMode;
  flipbook: boolean;
  /** what an inner page sheet grows by; 0 = the trim size. `outside` edges: nothing at the gutter */
  pageBleedMm: number;
  pageBleedEdges: 'all' | 'outside';
  /** what the cover strip (or a lone cover panel) grows by, per edge */
  coverBleed: Bleed;
  /** spine width the cover strip is rendered with */
  coverSpineMm: number;
  cropMarks: boolean;
  /** emit the album's PDF pages in reverse — see `PrintServiceProfile.leftBound` */
  reversePages: boolean;
}

/** For a shop's hardcover the strip also covers a board bigger than the pages (Blurb): grow by that, then the wrap. */
export function coverBleedFor(service: PrintServiceProfile, binding: Binding, page: { widthMm: number; heightMm: number }): Bleed {
  const spec = service.covers[binding];
  if (!spec) return NO_BLEED;
  const match = nearestServiceSize(service, page);
  const extra = binding === 'hardcover' && match.exact ? match.size.boardExtraMm : undefined;
  const h = spec.wrapMm + (extra?.perSideW ?? 0);
  const v = spec.wrapMm + (extra ? extra.totalH / 2 : 0);
  return { topMm: v, rightMm: h, bottomMm: v, leftMm: h };
}

export function resolveExport(doc: Pick<AlbumDocument, 'bleedMm' | 'cover' | 'print' | 'page' | 'spreads'>, options: ExportOptions): ResolvedExport {
  const service = printService(options.printService);
  const binding = doc.print.binding;
  if (!service) {
    const bleed = options.printReady ? doc.bleedMm : 0;
    return {
      service: null,
      binding,
      coverSpec: null,
      dpi: options.dpi,
      jpegQuality: options.jpegQuality,
      pageMode: options.pageMode,
      cover: doc.cover.enabled ? options.cover : 'none',
      flipbook: options.flipbook,
      pageBleedMm: bleed,
      pageBleedEdges: 'all',
      coverBleed: uniformBleed(bleed),
      coverSpineMm: doc.cover.spineWidthMm,
      cropMarks: options.printReady,
      reversePages: false,
    };
  }
  const spec = service.covers[binding] ?? service.covers.hardcover ?? service.covers.softcover ?? null;
  const modes = spec?.modes ?? ['none'];
  // a cover switched off stays off; otherwise the person's choice if the shop takes it, else the shop's default
  const cover: CoverMode = !doc.cover.enabled || options.cover === 'none' ? 'none' : modes.includes(options.cover) ? options.cover : modes[0]!;
  return {
    service,
    binding,
    coverSpec: spec,
    dpi: options.dpi,
    jpegQuality: options.jpegQuality,
    pageMode: service.pageMode,
    cover,
    flipbook: options.flipbook,
    pageBleedMm: service.page.bleedMm,
    pageBleedEdges: service.page.edges,
    coverBleed: cover === 'separate' ? coverBleedFor(service, binding, doc.page) : uniformBleed(service.page.bleedMm),
    coverSpineMm: spec ? spec.spineMm(innerPageCount(doc)) : doc.cover.spineWidthMm,
    cropMarks: service.cropMarks,
    reversePages: service.leftBound && doc.cover.binding === 'rtl',
  };
}

/**
 * The document the cover strip is rendered from: the same album with the spine at the width the
 * shop will bind it to, artwork slid with the panels as the editor does. Inner pages are unaffected.
 */
export function documentForExport(doc: AlbumDocument, options: ExportOptions): AlbumDocument {
  const resolved = resolveExport(doc, options);
  if (!resolved.service || resolved.coverSpineMm === doc.cover.spineWidthMm) return doc;
  return applyCommand(doc, { type: 'cover.set', patch: { spineWidthMode: 'manual', spineWidthMm: resolved.coverSpineMm } });
}

/** What the editor should draw for the album's chosen shop: the safe zone, and on the cover the wrap. */
export interface PrintZones {
  safeMarginMm: number;
  gutterSafeMarginMm: number;
  /** cover strip: how far artwork should extend beyond the trim to cover the wrap */
  coverWrapMm: number;
  /** inner pages: the bleed the shop trims off */
  pageBleedMm: number;
  pageBleedEdges: 'all' | 'outside';
}

export function printZones(doc: Pick<AlbumDocument, 'print' | 'page'>): PrintZones | null {
  const service = printService(doc.print.service);
  if (!service) return null;
  const bleed = coverBleedFor(service, doc.print.binding, doc.page);
  return {
    safeMarginMm: service.safeMarginMm,
    gutterSafeMarginMm: service.gutterSafeMarginMm ?? service.safeMarginMm,
    coverWrapMm: Math.max(bleed.topMm, bleed.rightMm),
    pageBleedMm: service.page.bleedMm,
    pageBleedEdges: service.page.edges,
  };
}
